<template>
  <b-container fluid class="layout-wide py-5">
    <div v-if="viewMode === 'list'" class="no-print">
      <h2 class="jinro-title font-weight-bold mb-4">장비대금청구서 내역</h2>

      <b-card class="border-0 shadow-sm mb-4" style="border-radius: 20px;">
        <b-row align-v="end">
          <b-col md="4">
            <b-form-group label="시작일" label-class="small font-weight-bold text-muted">
              <b-form-input type="date" v-model="search.from" class="jinro-input"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="종료일" label-class="small font-weight-bold text-muted">
              <b-form-input type="date" v-model="search.to" class="jinro-input"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4" class="mb-3 d-flex align-items-end">
            <b-button variant="primary" @click="loadInvoiceList" class="jinro-btn flex-grow-1 mr-2">조회하기</b-button>
            <b-button variant="success" @click="goWriteMode" class="jinro-btn flex-grow-1">새 청구서 작성</b-button>
          </b-col>
        </b-row>
      </b-card>
      <b-card class="border-0 shadow-sm mb-4" style="border-radius: 20px;">
        <b-row>
          <b-col md="3">
            <b-form-group label="청구 상태" label-class="small font-weight-bold text-muted" class="mb-0">
              <b-form-select v-model="search.billingStatus" :options="billingOptions" class="jinro-input">
                <template #first><option :value="null">전체</option></template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="지급 상태" label-class="small font-weight-bold text-muted" class="mb-0">
              <b-form-select v-model="search.paymentStatus" :options="paymentOptions" class="jinro-input">
                <template #first><option :value="null">전체</option></template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3" class="d-flex align-items-center pt-4">
            <b-form-checkbox v-model="search.excludeProxy" switch size="lg">
              대리청구 제외
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-card>

      <b-table :items="invoiceList" :fields="listFields" hover show-empty head-variant="light" class="bg-white shadow-sm rounded"
               @row-clicked="goDetail" style="cursor: pointer;">
        <template #empty>조회된 내역이 없습니다.</template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" @click.stop="goDetail(data.item)">상세보기</b-button>
        </template>
        <template #cell(billingStatus)="row">
          <div class="d-flex flex-column" @click.stop>
            <b-form-select
                size="sm"
                v-model="row.item.billingStatus"
                :options="billingOptions"
                @change="updateStatus(row.item, 'billingStatus')"
                class="mb-1"
            />
            <b-form-datepicker
                v-if="row.item.billingStatus === '완료'"
                size="sm"
                v-model="row.item.billingDate"
                @input="updateStatus(row.item, 'billingDate')"
                placeholder="날짜 선택"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                hide-header
                reset-button
            />
          </div>
        </template>

        <template #cell(paymentStatus)="row">
          <div class="d-flex flex-column" @click.stop>
            <b-form-select
                size="sm"
                v-model="row.item.paymentStatus"
                :options="paymentOptions"
                @change="updateStatus(row.item, 'paymentStatus')"
                class="mb-1"
            />
            <b-form-select
                v-if="row.item.paymentStatus !== '미완료' && row.item.paymentStatus !== '지급불가'"
                size="sm"
                v-model="row.item.paymentMethod"
                :options="['세금계산서', '현금', '계좌이체']"
                @change="updateStatus(row.item, 'paymentMethod')"
                class="mb-1"
            >
              <template #first><option :value="null">-방법-</option></template>
            </b-form-select>

            <b-form-datepicker
                v-if="row.item.paymentStatus === '완료'"
                size="sm"
                v-model="row.item.paymentDate"
                @input="updateStatus(row.item, 'paymentDate')"
                placeholder="지급일"
                hide-header
            />
          </div>
        </template>
      </b-table>

      <div class="text-right mt-3">
        <b-button variant="success" @click="goWriteMode" class="px-5 rounded-pill">새 청구서 작성</b-button>
      </div>
    </div>

    <div v-else class="invoice-wrapper">
      <div class="d-flex justify-content-between align-items-center mb-4 no-print">
        <b-button variant="light" @click="viewMode = 'list'" class="rounded-pill">
          <b-icon icon="arrow-left" class="mr-1"></b-icon>
          목록으로
        </b-button>
        <div class="d-flex align-items-center">
          <h4 class="mb-0 mr-3 font-weight-bold text-primary">
            {{ status === 'write' ? '신규 작성' : (status === 'view' ? '상세 보기' : '내용 수정') }}
          </h4>

          <b-button v-if="status === 'view'" variant="warning" @click="handleEdit" class="mr-2 px-4 rounded-pill">
            수정하기
          </b-button>
          <b-button v-if="status === 'view'" variant="danger" @click="deleteInvoice" class="mr-2 px-4 rounded-pill">
            삭제하기
          </b-button>
          <b-button v-if="status === 'edit' || status === 'write'" variant="primary" @click="saveInvoice" class="mr-2 px-4 rounded-pill">
            {{ status === 'edit' ? '수정 완료' : '데이터 저장' }}
          </b-button>
          <b-button v-if="status === 'edit'" variant="light" @click="cancelEdit" class="mr-2 px-4 rounded-pill">
            취소
          </b-button>

          <b-button v-if="status === 'view'" variant="success" @click="downloadInvoiceExcel" class="mr-2 px-4 rounded-pill">
            Excel
          </b-button>

          <b-button variant="dark" @click="executePrint" class="px-4 rounded-pill">인쇄</b-button>
        </div>
      </div>

      <div class="no-print">
        <b-card no-body class="border-0 shadow-sm mb-4" style="border-radius: 20px;">
          <b-card-body class="p-4">
            <b-row>
              <b-col md="5">
                <h5 class="mb-4 font-weight-bold text-dark">청구 설정</h5>
                <b-form-group label="공급자" label-class="small text-muted font-weight-bold">
                  <div v-if="status === 'view'" class="p-2 border-bottom font-weight-bold">
                    {{ providerInfo ? providerInfo.companyName : '' }}
                  </div>
                  <b-form-select v-else v-model="selectedProvider" :options="providerOptions" @change="onProviderChange"
                                 class="jinro-input"></b-form-select>
                </b-form-group>
                <b-form-group label="년/월 선택" label-class="font-weight-bold text-primary h5">
                  <div class="d-flex align-items-center">
                    <b-input-group append="년" class="year-group mr-2">
                      <b-form-input v-model.number="year" type="number" class="custom-input text-center"
                                    @input="validateYear" :disabled="status === 'view'"></b-form-input>
                    </b-input-group>
                    <b-input-group append="월" class="month-group mr-2">
                      <b-form-input v-model.number="month" type="number" @change="validateMonth"
                                    class="custom-input text-center" :disabled="status === 'view'"></b-form-input>
                    </b-input-group>
                    <b-form-datepicker button-only right locale="ko" @input="onDateSelected"
                                       :disabled="status === 'view'" button-variant="primary"
                                       class="calendar-btn"></b-form-datepicker>
                  </div>
                </b-form-group>
                <b-form-group label="거래처" label-class="small text-muted font-weight-bold">
                  <div v-if="status === 'view'" class="p-2 border-bottom font-weight-bold text-success">{{
                      customerName
                    }}
                  </div>
                  <b-form-select v-else v-model="selectedCustomer" :options="customerOptions"
                                 @change="onCustomerChange" class="jinro-input"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="7" class="pl-md-5 border-left-custom" v-if="providerInfo">
                <h5 class="font-weight-bold text-dark mb-4">공급자 상세정보</h5>
                <b-row class="provider-info-grid">
                  <b-col cols="6">
                    <span>등록번호</span>
                    <strong>{{ providerInfo.regNo }}</strong>
                  </b-col>
                  <b-col cols="6">
                    <span>상호</span>
                    <strong>
                      {{ providerInfo.companyName }}
                      <small class="text-muted">
                        {{ (providerInfo.replacementCompanyName && providerInfo.replacementCompanyName.trim().length > 0) ? `(${providerInfo.replacementCompanyName})` : '' }}
                      </small>
                    </strong>
                  </b-col>
                  <b-col cols="6">
                    <span>성명</span>
                    <div class="seal-container">
                      <strong class="mr-2">{{ providerInfo.ownerName }}</strong>

                      <img v-if="currentUser && currentUser.stampImage"
                           :src="currentUser.stampImage"
                           class="seal-image"
                           style="width: 40px; height: 40px; position: absolute; margin-left: -10px; margin-top: -5px;"/>

                      <div v-else class="seal-box"
                           style="display: inline-block; border: 1px solid red; border-radius: 50%; width: 25px; height: 25px; text-align: center; color: red; font-size: 12px;">
                        인
                      </div>
                    </div>
                  </b-col>
                  <b-col cols="6">
                    <span>업태/종목</span>
                    <strong>{{ providerInfo.bizType }} / {{ providerInfo.bizItem }}</strong>
                  </b-col>
                  <b-col cols="6">
                    <span>전화번호</span>
                    <strong>{{ providerInfo.tel || '-' }}</strong>
                  </b-col>
                  <b-col cols="6">
                    <span>팩스번호</span>
                    <strong>{{ providerInfo.fax || '-' }}</strong>
                  </b-col>
                  <b-col cols="12">
                    <span>사업장 주소</span>
                    <strong>{{ providerInfo.address }}</strong>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <b-card no-body class="border-0 shadow-sm mb-4" style="border-radius: 20px; overflow: hidden;">
          <b-table responsive hover :items="items" :fields="fields" class="jinro-table mb-0">
            <template #cell(date)="data">
              <span :class="{'text-danger': isWeekend(data.item.date)}" class="font-weight-bold">
                {{ data.item.date }}일 <small v-if="isWeekend(data.item.date)">({{ getDayName(data.item.date) }})</small>
              </span>
            </template>
            <template #cell(carNumber)="data">
              <b-form-input v-model="data.item.carNumber" :disabled="status === 'view'" size="sm"
                            class="table-input"></b-form-input>
            </template>
            <template #cell(workTime)="data">
              <div v-if="status !== 'view'" class="d-flex align-items-center justify-content-center" style="min-width: 160px;">
                <b-input-group size="sm" class="custom-spin-group">
                  <b-form-input v-model.number="data.item.hourPart" type="number" class="text-center no-spin-arrow"
                                placeholder="0"
                                @input="updateWorkTime(data.item)"></b-form-input>
                  <b-input-group-append>
                    <div class="d-flex flex-column border-left">
                      <b-button variant="link" class="p-0 px-1 arrow-btn" @click="adjustTime(data.item, 'h', 1)">▲</b-button>
                      <b-button variant="link" class="p-0 px-1 arrow-btn" @click="adjustTime(data.item, 'h', -1)">▼</b-button>
                    </div>
                  </b-input-group-append>
                </b-input-group>
                <span class="mx-1 font-weight-bold">:</span>
                <b-input-group size="sm" class="custom-spin-group">
                  <b-form-input v-model.number="data.item.minutePart" type="number" class="text-center no-spin-arrow"
                                placeholder="0"
                                @input="updateWorkTime(data.item)"></b-form-input>
                  <b-input-group-append>
                    <div class="d-flex flex-column border-left">
                      <b-button variant="link" class="p-0 px-1 arrow-btn" @click="adjustTime(data.item, 'm', 10)">▲</b-button>
                      <b-button variant="link" class="p-0 px-1 arrow-btn" @click="adjustTime(data.item, 'm', -10)">▼</b-button>
                    </div>
                  </b-input-group-append>
                </b-input-group>
              </div>
              <div v-else class="text-center font-weight-bold">{{ data.item.workTime || '0:00' }}</div>
            </template>
            <template #cell(unitPrice)="data">
              <b-form-input v-if="status !== 'view'"
                            :value="data.item.unitPrice ? formatComma(data.item.unitPrice) : ''"
                            @input="val => updateUnitPrice(data.item, val)"
                            size="sm"
                            class="text-right"
                            placeholder="0"></b-form-input>
              <div v-else class="text-right">{{ formatComma(data.item.unitPrice || 0) }}</div>
            </template>
            <template #cell(fuel)="data">
              <b-form-input v-if="status !== 'view'"
                            v-model.number="data.item.fuel"
                            type="number"
                            size="sm"
                            class="text-center"
                            placeholder="0"
                            @input="calculateRowBalance(data.item)"></b-form-input>
              <div v-else class="text-center">{{ data.item.fuel || 0 }}</div>
            </template>
            <template #cell(description)="data">
              <b-form-input v-model="data.item.description" :disabled="status === 'view'" size="sm"
                            class="table-input"></b-form-input>
            </template>
            <template #cell(balance)="data">
              <div class="font-weight-bold text-dark text-right">{{
                  (calculateRowBalance(data.item)).toLocaleString()
                }}
              </div>
            </template>
            <template #cell(tax)="data">
              <div class="text-muted text-right">{{
                  (Math.floor(calculateRowBalance(data.item) * 0.1)).toLocaleString()
                }}
              </div>
            </template>
          </b-table>
        </b-card>

        <b-card no-body class="border-0 shadow mt-4 text-white"
                style="border-radius: 20px; background: linear-gradient(135deg, #00A1E1 0%, #007bb1 100%);">
          <b-card-body class="p-4">
            <b-row class="text-center align-items-center">
              <b-col md="2">
                <div class="small opacity-75">유류량 총합</div>
                <div class="h4 font-weight-bold mb-0">{{ totalFuel }} ℓ</div>
              </b-col>
              <b-col md="2">
                <div class="small opacity-75">총 장비대</div>
                <div class="h4 font-weight-bold mb-0">{{ (totalBalance).toLocaleString() }}</div>
              </b-col>
              <b-col md="2">
                <div class="small opacity-75">세액(VAT)</div>
                <div class="h4 font-weight-bold mb-0">{{ (Math.floor(totalBalance * 0.1)).toLocaleString() }}</div>
              </b-col>
              <b-col md="3" class="border-left">
                <div class="h6 mb-1 opacity-75">합계 (Grand Total)</div>
                <div class="h3 font-weight-bold">{{ (grandTotal).toLocaleString() }} 원</div>
              </b-col>
              <b-col md="3">
                <b-button v-if="status !== 'view'" block size="lg" variant="success" @click="saveInvoice"
                          class="mb-2 rounded-pill font-weight-bold">저장
                </b-button>
                <b-button block size="lg" variant="light" @click="executePrint" class="rounded-pill font-weight-bold"
                          style="color: #00A1E1;">인쇄
                </b-button>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </div>

      <div id="print-area">
        <div id="real-print-content">
          <h1 class="text-center mb-5 print-main-title">장비대금 청구서</h1>

          <div class="d-flex justify-content-between mb-4 print-header-container">
            <div class="print-header-left">
              <div class="print-date-large" style="font-weight: 900 !important;">{{ year }}년 {{ month }}월분</div>
              <div class="print-customer-large" style="font-weight: 900 !important; border-bottom: 3px solid #000;">
                {{ customerName || '(거래처 미선택)' }} 귀하
              </div>
              <div class="print-notice" style="font-weight: 700;">아래와 같이 청구 합니다.</div>
            </div>

            <div v-if="providerInfo" class="print-header-right">
              <table class="provider-table-v2">
                <tr>
                  <td rowspan="5" class="table-label-v2">공<br>급<br>자</td>
                  <td class="table-sub-label-v2">등록번호</td>
                  <td colspan="3" class="table-value-v2 font-weight-bold">{{ providerInfo.regNo }}</td>
                </tr>
                <tr>
                  <td class="table-sub-label-v2">상호</td>
                  <td class="table-value-v2">
                    {{ providerInfo.companyName }}
                    <span style="font-size: 9pt;">
                      {{ (providerInfo.replacementCompanyName && providerInfo.replacementCompanyName.trim().length > 0) ? `(${providerInfo.replacementCompanyName})` : '' }}
                    </span>
                  </td>
                  <td class="table-sub-label-v2">성명</td>
                  <td class="table-value-v2 text-nowrap" style="position: relative; min-width: 120px;">
                    {{ providerInfo.ownerName }}
                    <span class="seal-container">
                <img v-if="currentUser && currentUser.stampImage" :src="currentUser.stampImage"
                     class="seal-image-print"/>
                <span v-else class="seal-mark">(인)</span>
              </span>
                  </td>
                </tr>
                <tr>
                  <td class="table-sub-label-v2">주소</td>
                  <td colspan="3" class="table-value-v2" style="font-size: 9.5pt;">{{ providerInfo.address }}</td>
                </tr>
                <tr>
                  <td class="table-sub-label-v2">업태</td>
                  <td class="table-value-v2">{{ providerInfo.bizType || '' }}</td>
                  <td class="table-sub-label-v2">종목</td>
                  <td class="table-value-v2">{{ providerInfo.bizItem || '' }}</td>
                </tr>
                <tr>
                  <td class="table-sub-label-v2">연락처</td>
                  <td class="table-value-v2">{{ providerInfo.tel || '-' }}</td>
                  <td class="table-sub-label-v2">FAX</td>
                  <td class="table-value-v2">{{ providerInfo.fax || '-' }}</td>
                </tr>
              </table>
            </div>
          </div>

          <table class="main-print-table">
            <thead>
            <tr>
              <th style="width: 10%">날짜</th>
              <th style="width: 15%">차량번호</th>
              <th style="width: 10%">시간</th>
              <th style="width: 12%">단가</th>
              <th style="width: 8%">유류(ℓ)</th>
              <th style="width: 12%">금액</th>
              <th style="width: 10%">세액</th>
              <th>작업내역</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in printItems" :key="'print-' + index">
              <td>
                {{ year }}-{{ String(month).padStart(2, '0') }}-{{ String(item.date).padStart(2, '0') }}
              </td>
              <td>{{ item.carNumber }}</td>
              <td>{{ item.workTime }}</td>
              <td class="text-right">{{ formatComma(item.unitPrice) }}</td>
              <td class="text-right">{{ item.fuel || 0 }}</td>
              <td class="text-right">{{ formatComma(calculateRowBalance(item)) }}</td>
              <td class="text-right">{{ formatComma(Math.floor(calculateRowBalance(item) * 0.1)) }}</td>
              <td class="text-left">{{ item.description }}</td>
            </tr>
            </tbody>
          </table>

          <div class="print-summary-section mt-1">
            <table class="summary-horizontal-table">
              <tr>
                <th class="summary-th">유류량 총합</th>
                <td class="summary-td">{{ totalFuel }} ℓ</td>
                <th class="summary-th">총 장비대</th>
                <td class="summary-td">{{ formatComma(totalBalance) }} 원</td>
                <th class="summary-th">세액(VAT)</th>
                <td class="summary-td">{{ formatComma(Math.floor(totalBalance * 0.1)) }} 원</td>
                <th class="summary-th highlight-label">최종 합계</th>
                <td class="summary-td highlight-value">￦ {{ formatComma(grandTotal) }}</td>
              </tr>
            </table>
          </div>

          <div class="print-footer-date">
            {{ new Date().getFullYear() }}년 {{ new Date().getMonth() + 1 }}월 {{ new Date().getDate() }}일
          </div>
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>

import { printA4 } from '@/utils/invoicePrint';

export default {
  name: 'InvoicePage',
  data() {
    const now = new Date();
    return {
      viewMode: 'list',
      status: 'write',
      search: {
        from: new Date(now.getFullYear() - 1, now.getMonth(), 2).toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
        billingStatus: null,
        paymentStatus: null,
        excludeProxy: true
      },
      invoiceList: [],
      listFields: [{key: 'workMonth', label: '작업월', formatter: (v) => v ? v.split('T')[0] : '', thClass: 'text-center', tdClass: 'text-center align-middle'},
        {key: 'carNumber', label: '차량번호', thClass: 'text-center', tdClass: 'text-center align-middle'},
        {key: 'companyName', label: '공급자 상호', thClass: 'text-center', tdClass: 'text-center align-middle'},
        {key: 'ownerName', label: '대표자', thClass: 'text-center', tdClass: 'text-center align-middle'},
        {key: 'customerName', label: '거래처', thClass: 'text-center', tdClass: 'text-center align-middle'},
        {key: 'totalWorkDays', label: '작업일수', formatter: value => value + '일', thClass: 'text-center', tdClass: 'text-center align-middle'},
        {key: 'totalBalance', label: '총 장비대', formatter: value => value.toLocaleString(), thClass: 'text-center', tdClass: 'text-right align-middle'},
        {key: 'totalTax', label: '총 세액', formatter: value => value.toLocaleString(), thClass: 'text-center', tdClass: 'text-right align-middle'},
        {key: 'totalAmount', label: '총 합계', formatter: value => value.toLocaleString(), variant: 'primary', thClass: 'text-center', tdClass: 'text-right align-middle'},
        {key: 'billingStatus', label: '청구 관리', thClass: 'text-center', tdClass: 'align-middle', thStyle: {width: '150px'}},
        {key: 'paymentStatus', label: '대금 지급 관리', thClass: 'text-center', tdClass: 'align-middle', thStyle: {width: '180px'}},
        {key: 'lastUpdatedAt', label: '최근 수정일', formatter: (value) => value ? value.replace('T', ' ').substring(0, 19) : '-', thClass: 'text-center', tdClass: 'text-center align-middle'}],
      currentUser: null,
      selectedProvider: null,
      selectedCustomer: null,
      customerName: '',
      carNumber: '',
      customerOptions: [{value: '', carNumber: '', text: '거래처를 선택하세요'}],
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      providerInfo: null,
      providerOptions: [{value: null, text: '공급자를 선택해 주세요'}],
      currentGroupId: null,
      items: [],
      billingOptions: ['미완료', '완료', '대리청구완료'],
      paymentOptions: ['미완료', '완료', '지급예정', '지급불가'],
      fields: [{
        key: 'date',
        label: '날짜',
        thClass: 'text-center',
        tdClass: 'text-center align-middle',
        thStyle: {width: '100px'}
      }, {key: 'carNumber', label: '차량번호', thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: {width: '130px'}}, {
        key: 'workTime',
        label: '시간',
        thClass: 'text-center',
        thStyle: {width: '160px'}
      }, {key: 'unitPrice', label: '단가', thClass: 'text-center', tdClass: 'text-center align-middle',thStyle: {width: '120px'}}, {
        key: 'fuel',
        label: '유류(ℓ)',
        thClass: 'text-center',
        thStyle: {width: '90px'}
      }, {key: 'balance', label: '잔액', thClass: 'text-center', tdClass: 'text-right'}, {
        key: 'tax',
        label: '세액',
        thClass: 'text-center',
        tdClass: 'text-center align-middle'
      }, {key: 'description', label: '작업내역', thClass: 'text-center', tdClass: 'text-right'}]
    }
  },
  computed: {
    totalFuel() {
      return this.items.reduce((acc, cur) => acc + (Number(cur.fuel) || 0), 0);
    },
    totalBalance() {
      return this.items.reduce((acc, cur) => acc + this.calculateRowBalance(cur), 0);
    },
    grandTotal() {
      const balance = this.totalBalance;
      return balance + Math.floor(balance * 0.1);
    },
    printItems() {
      return this.items.filter(item => item.carNumber || item.description);
    }
  },
  created() {
    this.generateDates();
    this.fetchInitData();
    this.loadCustomers();
    this.fetchMyInfo(); // 페이지 로드 시 내 정보 가져오기 추가
    this.loadInvoiceList();
  },
  methods: {
    async loadInvoiceList() {
      try {
        const res = await this.$axios.get('/api/invoices/search', {
          params: {
            from: this.search.from,
            to: this.search.to,
            userId: localStorage.getItem('userId'),
            billingStatus: this.search.billingStatus,
            paymentStatus: this.search.paymentStatus,
            excludeProxy: this.search.excludeProxy
          }
        })
        this.invoiceList = res.data.data;
      } catch (e) {
        alert("목록 로드 실패");
      }
    },
    goWriteMode() {
      this.viewMode = 'form';
      this.status = 'write';
      this.currentGroupId = null; // ID 초기화

      // [중요] 날짜를 '현재' 기준으로 다시 리셋
      const now = new Date();
      this.year = now.getFullYear();
      this.month = now.getMonth() + 1;

      // [중요] 선택된 공급자 및 거래처 정보 완벽 초기화
      this.selectedProvider = null;
      this.providerInfo = null;     // 공급자 상세 정보도 비워야 화면에서 사라짐
      this.selectedCustomer = '';   // 거래처 선택값 초기화 (null 또는 '')
      this.customerName = '';
      this.carNumber = '';          // 거래처 메모(차량번호) 초기화

      // 초기화된 날짜(이번달) 기준으로 그리드 다시 생성
      this.generateDates();
    },
    async fetchMyInfo() {
      try {
        const res = await this.$axios.get('/api/auth/me');
        if (res.data && res.data.status === "success") {
          this.currentUser = res.data.data;
        }
      } catch (e) {
        console.error("내 정보 로드 실패:", e);
      }
    },
    async goDetail(record) {
      this.viewMode = 'form';
      this.status = 'view'; // 처음엔 조회 모드

      try {
        const res = await this.$axios.get(`/api/invoice/group/${record.invoiceGroupId}`);
        // 서버에서 받은 실제 저장된 데이터만 표시
        this.items = res.data.data.map(item => {
          this.prepareTimeParts(item);
          return item;
        });

        if (this.items.length > 0) {
          const first = this.items[0];
          // 연/월 정보 업데이트 (나중에 수정 모드 전환 시 필요)
          const dateParts = first.workDate.split('-');
          this.year = parseInt(dateParts[0]);
          this.month = parseInt(dateParts[1]);

          this.providerInfo = first.provider;
          this.selectedProvider = first.provider?.id;
          this.selectedCustomer = first.customer?.id;
          this.customerName = first.customer?.name || '미지정';
          this.carNumber = first.customer?.memo;
          this.currentGroupId = record.invoiceGroupId;

          if (first.user) {
            this.currentUser = first.user;
          }
        }
      } catch (e) {
        console.error("상세 로드 실패:", e);
        alert("상세 로드 실패");
      }
    },
    // 1. 수정하기 버튼 클릭 시 실행
    handleEdit() {
      this.status = 'edit'; // 상태 변경

      // 현재 보고 있는 청구서의 연/월 기준으로 마지막 날짜 계산
      const lastDay = new Date(this.year, this.month, 0).getDate();
      const localDateList = Array.from({length: lastDay}, (_, i) => i + 1);

      // 기존에 화면에 보이던 데이터(저장된 데이터)를 복사
      const savedItems = [...this.items];

      // 1일부터 말일까지 확장 병합
      this.items = localDateList.map(dateNum => {
        const savedItem = savedItems.find(item => item.date === dateNum);

        if (savedItem) {
          return {
            ...savedItem,
            hourPart: savedItem.hourPart === 0 ? null : savedItem.hourPart,
            minutePart: savedItem.minutePart === 0 ? null : savedItem.minutePart,
            unitPrice: savedItem.unitPrice === 0 ? null : savedItem.unitPrice,
            fuel: savedItem.fuel === 0 ? null : savedItem.fuel,
          };
        } else {
          // 데이터가 없는 날짜는 요청하신 대로 차량번호/단가 등을 빈 값으로 생성
          return {
            date: dateNum,
            carNumber: '',
            unitPrice: null, // [수정] 0 -> null
            fuel: null, // [수정] 0 -> null
            description: '',
            hourPart: null, // [수정]
            minutePart: null, // [수정]
            workTime: ''
          };
        }
      });
    },

    // 2. 취소 버튼 클릭 시 실행
    cancelEdit() {
      // 기존에 보고 있던 그룹ID로 다시 상세 정보를 불러와서 초기화
      this.goDetail({invoiceGroupId: this.currentGroupId});
    },
    async deleteInvoice() {
      if (!this.currentGroupId) {
        alert("삭제할 청구서 정보가 없습니다.");
        return;
      }

      if (!confirm("정말로 이 청구서를 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.")) {
        return;
      }

      try {
        // 백엔드 API 호출 (그룹 ID로 삭제)
        const res = await this.$axios.delete(`/api/invoice/group/${this.currentGroupId}`);

        if (res.data && res.data.status === 'success') {
          alert("삭제되었습니다.");
          this.viewMode = 'list'; // 목록 화면으로 전환
          this.loadInvoiceList(); // 목록 새로고침
        } else {
          alert("삭제에 실패했습니다: " + (res.data.message || 'Unknown error'));
        }
      } catch (e) {
        console.error("삭제 중 오류 발생:", e);
        alert("삭제 중 오류가 발생했습니다.");
      }
    },
    generateDates() {
      const lastDay = new Date(this.year, this.month, 0).getDate();
      this.items = Array.from({length: lastDay}, (_, i) => ({
        date: i + 1,
        carNumber: '',
        workTime: '', // [수정] 0:00 -> 빈값
        hourPart: null, // [수정] 0 -> null
        minutePart: null, // [수정] 0 -> null
        unitPrice: null, // [수정] 0 -> null
        fuel: null, // [수정] 0 -> null
        description: ''
      }));
    },
    onDateSelected(value) {
      if (value) {
        const selected = new Date(value);
        this.year = selected.getFullYear();
        this.month = selected.getMonth() + 1;
        this.generateDates();
      }
    },
    validateYear() {
      if (this.year > 9999) this.year = 9999;
      this.generateDates();
    },
    validateMonth() {
      if (this.month > 12) this.month = 12; else if (this.month < 1) this.month = 1;
      this.generateDates();
    },
    isWeekend(day) {
      const dayNum = new Date(this.year, this.month - 1, day).getDay();
      return dayNum === 0 || dayNum === 6;
    },
    getDayName(day) {
      const days = ['일', '월', '화', '수', '목', '금', '토'];
      return days[new Date(this.year, this.month - 1, day).getDay()];
    },
    async fetchInitData() {
      try {
        const res = await this.$axios.get('/api/providers', {
          params: {
            userId: localStorage.getItem('userId')
          }
        })
        this.providerOptions = [{value: null, text: '공급자를 선택해 주세요'}, ...res.data.data.providers.map(p => ({
          value: p.id,
          text: p.companyName,
          detail: p
        }))];
      } catch (e) {
        console.error(e);
      }
    },
    onProviderChange(val) {
      const p = this.providerOptions.find(opt => opt.value === val);
      this.providerInfo = p ? p.detail : null;
    },
    onCustomerChange(val) {
      const customer = this.customerOptions.find(c => c.value === val);
      if (customer && customer.value) {
        this.customerName = customer.text;
        this.carNumber = customer.carNumber;
      } else {
        this.customerName = '';
        this.carNumber = '';
      }
    },
    async loadCustomers() {
      try {
        const res = await this.$axios.get('/api/customers', {
          params: {
            userId: localStorage.getItem('userId'),
            includeInactive: true
          }
        })
        this.customerOptions = [{value: '', text: '거래처를 선택하세요'}, ...res.data.data.map(c => ({
          value: c.id,
          text: c.name,
          carNumber: c.memo,
          fullData: c
        }))];
      } catch (e) {
        console.error(e);
      }
    },
    async saveInvoice() {
      const validItems = this.items.filter(i => i.carNumber || i.description);

      if (!this.selectedProvider || !this.selectedCustomer) {
        alert("공급자와 거래처를 모두 선택해주세요.");
        return;
      }

      try {
        const payload = {
          providerId: this.selectedProvider,
          customerId: this.selectedCustomer,
          customerName: this.customerName,
          invoiceGroupId: this.currentGroupId,
          year: this.year,
          month: this.month,
          items: validItems
        };

        await this.$axios.post('/api/saveInvoice', payload);
        alert('저장되었습니다.');
        this.viewMode = 'list';
        this.loadInvoiceList();
      } catch (e) {
        alert('저장 실패');
      }
    },
    adjustTime(item, type, delta) {
      if (type === 'h') {
        item.hourPart = Math.max(0, (item.hourPart || 0) + delta);
      } else {
        let m = (item.minutePart || 0) + delta;
        item.minutePart = m >= 60 ? 0 : (m < 0 ? 50 : m);
      }
      this.updateWorkTime(item);
    },
    updateWorkTime(item) {
      const mins = String(item.minutePart || 0).padStart(2, '0');
      item.workTime = `${item.hourPart || 0}:${mins}`;
    },
    prepareTimeParts(item) {
      if (item.workTime?.includes(':')) {
        const [h, m] = item.workTime.split(':').map(Number);
        item.hourPart = h;
        item.minutePart = m;
      }
    },
    calculateRowBalance(item) {
      if (!item.workTime) return 0;
      const [h, m] = item.workTime.split(':').map(Number);
      const decimalTime = (h || 0) + ((m || 0) / 60);
      return Math.floor(decimalTime * (item.unitPrice || 0));
    },
    formatComma(val) {
      return val?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || '0';
    },
    updateUnitPrice(item, val) {
      // 빈 문자열이거나 값이 없으면 null 처리
      if (!val || val === '') {
        item.unitPrice = null;
        return;
      }

      const newPrice = parseInt(val.replace(/[^0-9]/g, ''), 10) || 0;
      item.unitPrice = newPrice;

      if (newPrice > 0 && !item.carNumber && this.carNumber) {
        item.carNumber = this.carNumber;
      }
    },
    executePrint() {
      // printA4 함수에 두 번째 인자로 파일명 생성 데이터를 전달합니다.
      printA4('real-print-content', {
        year: this.year,          // 현재 선택된 년도 (예: 2025)
        month: this.month,        // 현재 선택된 월 (예: 5)
        customerName: this.customerName // 거래처명 (예: 에스피네이처)
      })
    },
    // 상태 업데이트 메서드
    async updateStatus(item, fieldChanged) {
      const payload = {
        invoiceGroupId: item.invoiceGroupId,
        billingStatus: item.billingStatus,
        billingDate: item.billingDate,
        paymentStatus: item.paymentStatus,
        paymentDate: item.paymentDate,
        paymentMethod: item.paymentMethod
      };

      try {
        // 여기도 api.post 가 아니라 this.$axios.post 입니다.
        await this.$axios.post('/api/invoice/status', payload);

        if(fieldChanged === 'billingStatus' && item.billingStatus === '완료') {
          await this.loadInvoiceList();
        }
      } catch (e) {
        console.error(e);
        alert('상태 업데이트 실패');
      }
    },
    // [신규] 청구서 엑셀 다운로드
    async downloadInvoiceExcel() {
      if (!this.currentGroupId) {
        alert("청구서 정보가 없습니다.");
        return;
      }
      try {
        const response = await this.$axios.get(`/api/invoice/excel/${this.currentGroupId}`, {
          responseType: 'blob' // 파일 다운로드 필수 설정
        });

        // 1. 헤더에서 파일명 추출
        const contentDisposition = response.headers['content-disposition'];
        let fileName = `청구서_${this.customerName}_${this.year}${this.month}.xlsx`; // 기본값 (추출 실패 시 사용)

        if (contentDisposition) {
          // "filename=" 뒤에 있는 값을 정규식으로 찾음
          const fileNameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
          if (fileNameMatch && fileNameMatch.length === 2) {
            // URL 디코딩 (백엔드에서 URLEncoder로 인코딩했으므로 필수)
            // .replace(/\+/g, ' ') : 공백이 + 기호로 변환된 것을 다시 공백으로 변경
            fileName = decodeURIComponent(fileNameMatch[1].replace(/\+/g, ' '));
          }
        }

        // 다운로드 링크 생성 및 클릭
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // 파일명은 백엔드 헤더에서 오지만, 안전하게 여기서도 지정 가능
        // const fileName = `청구서_${this.customerName}_${this.year}${this.month}.xlsx`;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (e) {
        console.error("엑셀 다운로드 실패", e);
        alert("엑셀 다운로드 중 오류가 발생했습니다.");
      }
    }
  }
}
</script>

<style scoped>
/* 화면 레이아웃 스타일 */
.jinro-title {
  color: #00A1E1;
  position: relative;
  display: inline-block;
  padding-bottom: 15px;
}

.jinro-title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 6px;
  background-color: #00A1E1;
  border-radius: 10px;
}

.jinro-input {
  border-radius: 12px;
  height: 45px;
  border: 1px solid #e0e0e0;
}

.table-input {
  border: 1px solid transparent;
  background-color: #f9f9f9;
  border-radius: 8px;
  height: 35px;
  width: 100%;
}

.table-input:disabled {
  background-color: #f1f1f1;
  cursor: not-allowed;
}

.jinro-table {
  table-layout: fixed;
  width: 100%;
}

.seal-box {
  width: 40px;
  height: 40px;
  border: 2px solid #d32f2f;
  color: #d32f2f;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.provider-info-grid span {
  font-size: 0.75rem;
  color: #888;
  display: block;
}

.provider-info-grid strong {
  font-size: 1rem;
  color: #333;
  margin-bottom: 10px;
  display: block;
}

.border-left-custom {
  border-left: 1px solid #eee;
}

.custom-spin-group {
  width: 70px !important;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
  background: white;
}

.no-spin-arrow::-webkit-inner-spin-button, .no-spin-arrow::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.custom-spin-group .form-control {
  border: none !important;
  padding: 0;
  height: 32px;
}

.arrow-btn {
  font-size: 8px;
  line-height: 1;
  color: #666;
  text-decoration: none !important;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.arrow-btn:hover {
  background: #e9ecef;
}

.border-left {
  border-left: 1px solid #ced4da !important;
}

.year-group {
  width: 140px !important;
}

.month-group {
  width: 110px !important;
}

.custom-input {
  font-size: 1.3rem !important;
  font-weight: bold;
  height: 55px !important;
  padding: 0;
}

.calendar-btn {
  height: 55px;
  width: 55px;
}

.text-danger {
  color: #ff4d4f !important;
}

/* [인쇄 설정] */
#print-area {
  display: none;
}

/* 평소에는 숨김 */

/* InvoicePage.vue <style scoped> 에 추가 */
.seal-container {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.seal-image {
  position: absolute;
  right: -20px; /* 이름 오른쪽에 살짝 겹치게 배치 */
  width: 40px;
  height: 40px;
  opacity: 0.8; /* 실제 도장처럼 약간 투명하게 설정 가능 */
  z-index: 10;
}
</style>
